import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { NotifyService } from 'src/app/services/notify.service';
import { ValueService } from 'src/app/services/value.service';
import { environment } from 'src/environments/environment';
declare let $: any;
@Component({
  selector: 'app-register-page',
  templateUrl: './register-page.component.html',
  styleUrls: ['./register-page.component.css'],
})
export class RegisterPageComponent implements OnInit {
  registerForm: FormGroup;
  private registerApi = environment.url + environment.register;
  /**
   * Creates a new instance of the RegisterPageComponent.
   * @param router The `Router` service for navigating to different routes.
   * @param fb The `FormBuilder` service for creating a form.
   * @param makeapi The `ApiService` service for making API calls.
   * @param getdata The `ValueService` service for getting data.
   * @param notify The `NotifyService` service for showing notifications.
   */
  constructor(
    private router: Router,
    private fb: FormBuilder,
    private makeapi: ApiService,
    private getdata: ValueService,
    private notify: NotifyService
  ) {
    /**
     * Creates a form with the specified fields and validators.
     * The fields are:
     * - startupName: The startup name.
     * - name: The name of the user.
     * - phone: The phone number of the user.
     * - email: The email address of the user.
     * - roleid: The role id of the user.
     * - incubatorRegistrationType: The registration type of the incubator.
     * - incubatorRegistrationTypeOther: The registration type of the incubator if it is not one of the options.
     * - incubatorRegDesc: The description of the incubator registration.
     * - incubatorRegFile: The file for the incubator registration.
     * - incubatorRegFileName: The name of the file for the incubator registration.
     */
    this.registerForm = this.fb.group({
      startupName: [''],
      name: ['', [Validators.required]],
      phone: [
        '',
        [
          Validators.required,
          Validators.pattern('^((\\+91-?)|0)?[0-9]{10,10}$'),
        ],
      ],
      email: ['', [Validators.required, Validators.email]],
      roleid: [''],
      incubatorRegistrationType: [''],
      incubatorRegistrationTypeOther: [''],
      incubatorRegDesc: ['', [Validators.minLength(250)]],
      incubatorRegFile: [''],
      incubatorRegFileName: [''],
    });
  }
  get add() {
    return this.registerForm.controls;
  }
  /**
   * Called when the component is initialized.
   * Sets the `isVisibleSource` subject of the `ValueService` to `true`.
   * Calls the `checkRole` method with the argument 'Incubator' to set the form fields accordingly.
   */
  ngOnInit(): void {
    // Show the loading indicator
    this.getdata.isVisibleSource.next(true);

    // Set the form fields based on the role 'Incubator'
    this.checkRole('Incubator');
  }
  /**
   * Handles the selection of a role and updates the form accordingly.
   * If the role is 'Incubator', the fields for registration type, description, and file are set as required.
   * If the role is not 'Incubator', the fields for registration type, description, and file are cleared and their validators are removed.
   * @param name The value of the role.
   */
  checkRole(name: string) {
    // Reset the form and delete any existing image.
    this.registerForm.reset();
    this.deleteImage();

    // Determine the role and set the validators accordingly.
    if (name === 'Incubator') {
      // Set the role to 2 and add the validators.
      let getform = this.registerForm.value;
      getform.roleid = 2;
      this.registerForm.patchValue(getform);
      this.registerForm.controls['incubatorRegistrationType'].setValidators([
        Validators.required,
      ]);
      this.registerForm.controls['incubatorRegDesc'].setValidators([
        Validators.required,
        Validators.minLength(250),
      ]);
      this.registerForm.controls['incubatorRegFileName'].setValidators([
        Validators.required,
      ]);
    } else {
      // Set the role to 4 and remove the validators.
      let getform = this.registerForm.value;
      getform.roleid = 4;
      this.registerForm.patchValue(getform);
      this.registerForm.controls['incubatorRegistrationType'].clearValidators();
      this.registerForm.controls['incubatorRegDesc'].clearValidators();
      this.registerForm.controls['incubatorRegFileName'].clearValidators();
    }

    // Update the validity of the fields.
    this.registerForm.controls[
      'incubatorRegistrationType'
    ].updateValueAndValidity();
    this.registerForm.controls['incubatorRegDesc'].updateValueAndValidity();
    this.registerForm.controls['incubatorRegFileName'].updateValueAndValidity();
  }
  showOtherRegType = false;
  colSet = 'col-lg-12 col-md-12 col-12';
  /**
   * Handles the selection of a registration type and updates the form accordingly.
   * If the value is 'Other', the field for entering the other registration type is displayed and set as required.
   * If the value is not 'Other', the field for entering the other registration type is hidden and the value is cleared.
   * @param value The value of the registration type.
   */
  selectRegistrationType(value: string) {
    let val = value;
    if (val == 'Other') {
      // Show the field for entering the other registration type and set it as required
      this.showOtherRegType = true;
      this.colSet = 'col-lg-6 col-md-6 col-12';
      this.registerForm.controls[
        'incubatorRegistrationTypeOther'
      ].setValidators([Validators.required]);
    } else {
      // Hide the field for entering the other registration type and clear the value
      this.showOtherRegType = false;
      this.colSet = 'col-lg-12 col-md-12 col-12';
      this.registerForm.controls['incubatorRegistrationTypeOther'].setValue('');
      this.registerForm.controls[
        'incubatorRegistrationTypeOther'
      ].clearValidators();
    }
    // Update the validation status of the form
    this.registerForm.controls[
      'incubatorRegistrationTypeOther'
    ].updateValueAndValidity();
  }
  /**
   * Handles the registration form submission.
   * If the form is invalid, displays an error message.
   * If the form is valid, makes an API call to register the user and displays a success or error message based on the API response.
   */
  registerAdmin() {
    let getform = this.registerForm.value;

    // Check if the form is invalid
    if (this.registerForm.invalid) {
      // If the form is invalid, mark all the fields as touched and show an error message
      this.registerForm.markAllAsTouched();
      this.notify.notifyError('Please fill all the mandatory fields !!');
      return;
    }

    // If the form is valid, make an API call to register the user
    this.getdata.amizhthLoader('show');
    this.makeapi
      .method(this.registerApi, getform, 'login')
      .subscribe((data) => {
        // Hide the loader
        this.getdata.amizhthLoader('hide');
        // If the API call is successful, show a success message
        if (data.responseStatus == 'Success') {
          document.getElementById('RegisterModal').style.display = 'block';
        } else {
          // If the API call is not successful, show an error message
          this.notify.notifyError(data.responseMessage);
        }
      });
  }
  /**
   * Resets the registration form and navigates to the home page.
   */
  cancelReg() {
    // Reset the registration form
    this.registerForm.reset();
    // Navigate to the home page
    this.router.navigateByUrl('/home');
  }
  selecetdFile: any = [];
  url: any;
  FileValue: any = '';
  FileName: any;
  documentValues = [];
  disName;
  /**
   * Handles the file upload event.
   * @param event The file upload event.
   */
  fileUpload(event) {
    this.selecetdFile = event.target.files[0];

    if (event.target.files && this.selecetdFile) {
      const reader = new FileReader();
      const imagetype = this.selecetdFile.type;
      const imagedatatype = imagetype.split('/');
      const img_crt_type = imagedatatype[1];

      // Validate the file type
      if (
        img_crt_type === 'jpeg' ||
        img_crt_type === 'jpg' ||
        img_crt_type === 'png' ||
        img_crt_type === 'pdf'
      ) {
        // Read the file as a data URL
        reader.readAsDataURL(this.selecetdFile);
        reader.onload = () => {
          this.url = reader.result;
          // Split the data URL into its parts
          const spl = this.url.split(',');
          // Set the file value and name
          this.FileValue = spl[1];
          this.FileName = this.selecetdFile.name;
          this.disName = this.selecetdFile.name;
          // Update the form with the uploaded file
          const getform = this.registerForm.value;
          getform.incubatorRegFileName = this.selecetdFile.name;
          getform.incubatorRegFile = this.FileValue;
          this.registerForm.patchValue(getform);
        };
      } else {
        // Notify the user if the file type is not allowed
        this.notify.notifyError(
          'Only *jpeg,*jpg,*png,*pdf formets are allowed'
        );
      }
    }
  }
  /**
   * Resets the file input and clears the related form fields.
   */
  deleteImage() {
    // Clear file-related variables
    this.FileValue = '';
    this.FileName = '';
    this.disName = '';
    this.selecetdFile = '';

    // Reset the file input element
    $('#input-file-id').val('');

    // Retrieve current form values
    let getform = this.registerForm.value;

    // Clear file-related form fields
    getform.incubatorRegFileName = '';
    getform.incubatorRegFile = '';

    // Update the form with the cleared values
    this.registerForm.patchValue(getform);
  }

  /**
   * Closes the modal by navigating to the login page.
   */
  closeModal(): void {
    // Navigate to the login page
    this.router.navigateByUrl('/login');
  }
}
